/**
 * Shared layout for a single report drill-down.
 *
 * Layout breakpoints (driven by useResponsive — isMobile triggers ≤1024px):
 *
 * Desktop (>1024px):
 *  ┌──────────────────────────────────────────────────────────────────────┐
 *  │  ← POS Reports / <Title>                                             │
 *  │  <Title>                                                             │
 *  │  <subtitle>                                                          │
 *  │                                                                      │
 *  │  ┌─ Filters ─────────────────────────────┐  ┌─ Included columns ──┐  │
 *  │  │  From  To  Location  Format            │  │  • ...               │
 *  │  │  [error if any]                        │  │  • ...               │
 *  │  │  [Generate]   ✓ Downloaded             │  └──────────────────────┘
 *  │  └────────────────────────────────────────┘                          │
 *  └──────────────────────────────────────────────────────────────────────┘
 *
 * Mobile (≤1024px):
 *  - Filter card stacks above the bullets card.
 *  - All inputs go full-width.
 *  - Format buttons share a row (2 buttons, 50/50).
 *  - Generate button is full-width.
 *
 * The whole page is `height: 100%` + `overflowY: auto` because the host
 * shell has `overflow: hidden` on the content slot — each library page
 * is responsible for its own scroll.
 */

import React, { useEffect, useMemo, useState } from 'react';
import financeReportsService, {
  ExportFormat,
  ReportType,
} from '../../../services/financeReports.service';
import { getLocations as getLocationsService } from '../../../services/inventoryCorrections.service';
import { webNavigate } from '../../../services/navigate.service';
import { showToast } from '../../../services/toast.service';
import { showApiError } from '../../../services/apiError';
import { useResponsive } from '../../../hook/useResponsive';
import { useLanguage } from './useLanguage';

interface Location {
  id: string;
  locationName?: string;
  name?: string;
}

interface ReportPageShellProps {
  reportType: ReportType;
  title: string;
  subtitle: string;
  bullets?: string[];
  xlsxOnly?: boolean;
  maxRangeDays?: number;
}

function toYyyyMmDd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Quick-filter preset id. 'custom' = user picked dates manually. */
type DatePreset =
  | 'today'
  | 'yesterday'
  | 'this-week'
  | 'last-week'
  | 'mtd'
  | 'custom';

/**
 * Resolve a preset to a {from, to} pair of YYYY-MM-DD strings.
 *
 * Week conventions: ISO weeks (Monday → Sunday). "This week" runs from
 * the most recent Monday to today (capped at today, not the rest of the
 * week). "Last week" is the full prior Monday → Sunday.
 *
 * MTD = first of the current month → today.
 */
function rangeForPreset(p: DatePreset, now: Date = new Date()): {
  from: string;
  to: string;
} | null {
  if (p === 'custom') return null;

  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  if (p === 'today') {
    return { from: toYyyyMmDd(today), to: toYyyyMmDd(today) };
  }

  if (p === 'yesterday') {
    const y = new Date(today);
    y.setDate(today.getDate() - 1);
    return { from: toYyyyMmDd(y), to: toYyyyMmDd(y) };
  }

  // ISO week: Monday = 1, Sunday = 7. JS Sunday = 0, convert to 7.
  const dow = today.getDay() === 0 ? 7 : today.getDay();

  if (p === 'this-week') {
    const monday = new Date(today);
    monday.setDate(today.getDate() - (dow - 1));
    return { from: toYyyyMmDd(monday), to: toYyyyMmDd(today) };
  }

  if (p === 'last-week') {
    const lastMonday = new Date(today);
    lastMonday.setDate(today.getDate() - (dow - 1) - 7);
    const lastSunday = new Date(lastMonday);
    lastSunday.setDate(lastMonday.getDate() + 6);
    return { from: toYyyyMmDd(lastMonday), to: toYyyyMmDd(lastSunday) };
  }

  if (p === 'mtd') {
    const first = new Date(today.getFullYear(), today.getMonth(), 1);
    return { from: toYyyyMmDd(first), to: toYyyyMmDd(today) };
  }

  return null;
}

/**
 * Reverse-lookup: which preset (if any) matches the current From/To pair?
 * Returns 'custom' when nothing matches — used to keep the chip strip
 * in sync after manual edits.
 */
function presetForRange(from: string, to: string): DatePreset {
  const presets: DatePreset[] = [
    'today',
    'yesterday',
    'this-week',
    'last-week',
    'mtd',
  ];
  for (const p of presets) {
    const r = rangeForPreset(p);
    if (r && r.from === from && r.to === to) return p;
  }
  return 'custom';
}

/**
 * Convert a YYYY-MM-DD date-input value to an ISO timestamp with the
 * caller's local timezone offset. We deliberately do NOT force UTC
 * midnight — hospitality businesses trade across midnight, so the user's
 * local-day window is what they intend.
 *
 * `endOfDay=true` returns the NEXT day's 00:00 local. The backend filters
 * half-open [from, to) so this cleanly captures the entire picked day.
 */
/** YYYY-MM-DD → dd/mm/yyyy (no timezone conversion; just reformat). */
function toDdMmYyyy(yyyyMmDd: string): string {
  if (!yyyyMmDd) return '';
  const [y, m, d] = yyyyMmDd.split('-');
  if (!y || !m || !d) return yyyyMmDd;
  return `${d}/${m}/${y}`;
}

/**
 * Inclusive calendar days between two YYYY-MM-DD strings, from the date
 * parts alone (no millisecond arithmetic, so DST never shifts the count).
 * Mirrors the API's max-range check, which counts days in `tz`.
 */
function inclusiveDays(from: string, to: string): number {
  const [y1, m1, d1] = from.split('-').map(Number);
  const [y2, m2, d2] = to.split('-').map(Number);
  return Math.round((Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1)) / 86400000) + 1;
}

function toIsoWithOffset(yyyyMmDd: string, endOfDay: boolean): string {
  const [y, m, d] = yyyyMmDd.split('-').map(Number);
  const local = endOfDay
    ? new Date(y, m - 1, d + 1, 0, 0, 0, 0)
    : new Date(y, m - 1, d, 0, 0, 0, 0);
  return local.toISOString();
}

const ReportPageShell: React.FC<ReportPageShellProps> = ({
  reportType,
  title,
  subtitle,
  bullets,
  xlsxOnly,
  maxRangeDays = 366,
}) => {
  const { language, t } = useLanguage();
  const { isMobile } = useResponsive();

  const companyId = localStorage.getItem('companyId') || '';
  const userId = localStorage.getItem('userId') || '';
  const languagePrefix = localStorage.getItem('language') || 'en';

  // Default landing on "This week" — most common end-of-day workflow.
  const initialRange = rangeForPreset('this-week') ?? {
    from: toYyyyMmDd(new Date()),
    to: toYyyyMmDd(new Date()),
  };
  const [dateFrom, setDateFromRaw] = useState<string>(initialRange.from);
  const [dateTo, setDateToRaw] = useState<string>(initialRange.to);
  const [preset, setPreset] = useState<DatePreset>('this-week');

  /** Apply a preset's date pair (or just flip to 'custom'). */
  const applyPreset = (p: DatePreset) => {
    setPreset(p);
    if (p === 'custom') return;
    const r = rangeForPreset(p);
    if (!r) return;
    setDateFromRaw(r.from);
    setDateToRaw(r.to);
  };

  /** Manual date edits drop us out of any preset (or land on one if it matches). */
  const setDateFrom = (v: string) => {
    setDateFromRaw(v);
    setPreset(presetForRange(v, dateTo));
  };
  const setDateTo = (v: string) => {
    setDateToRaw(v);
    setPreset(presetForRange(dateFrom, v));
  };

  const [locations, setLocations] = useState<Location[]>([]);
  const [locationId, setLocationId] = useState<string>('');
  const [format, setFormat] = useState<ExportFormat>('xlsx');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFilename, setLastFilename] = useState<string | null>(null);
  const [truncated, setTruncated] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res: any = await getLocationsService(userId, companyId, 1, 200, '');
        const list =
          res?.data?.data?.locations ??
          res?.data?.locations ??
          res?.data?.data ??
          res?.data ??
          [];
        if (!cancelled && Array.isArray(list)) {
          setLocations(list);
        }
      } catch {
        // ignored — fallback "All locations" still works
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, userId]);

  const rangeError = useMemo<string | null>(() => {
    if (!dateFrom || !dateTo) return null;
    if (dateFrom > dateTo) return t('From date must be before To date');
    if (inclusiveDays(dateFrom, dateTo) > maxRangeDays) {
      return t(
        'Date range exceeds maximum',
        `Date range is capped at ${maxRangeDays} days for this report.`,
      );
    }
    return null;
  }, [dateFrom, dateTo, maxRangeDays, t]);

  const canSubmit =
    !loading && !rangeError && !!companyId && !!dateFrom && !!dateTo;

  const handleGenerate = async () => {
    setError(null);
    setLastFilename(null);
    setTruncated(false);
    if (!canSubmit) return;
    setLoading(true);
    try {
      const { filename, truncated: wasTruncated } = await financeReportsService.exportReport({
        reportType,
        format,
        dateFrom: toIsoWithOffset(dateFrom, false),
        dateTo: toIsoWithOffset(dateTo, true),
        // Send the user-picked dates as dd/mm/yyyy labels so the export's
        // metadata block reflects exactly what was on screen, independent
        // of server-timezone interpretation of the ISO timestamps above.
        dateFromLabel: toDdMmYyyy(dateFrom),
        dateToLabel: toDdMmYyyy(dateTo),
        locationId: locationId || null,
        // Server uses this to translate metadata labels + column headers
        // inside the generated XLSX / PDF. Falls back to 'en' on the
        // backend if missing or unsupported.
        locale: localStorage.getItem('language') || 'en',
        // The browser's zone: the server renders per-row timestamps in it
        // and counts the window's calendar days in it (see inclusiveDays).
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      setLastFilename(filename);
      setTruncated(wasTruncated);
      if (wasTruncated) {
        showToast({
          message: t(
            'Export truncated — narrow the date range',
            'Export truncated — narrow the date range',
          ),
          type: 'warning',
          duration: 20000,
        });
      }
    } catch (e: any) {
      // The service has already decoded a JSON error blob into response.data,
      // so the API's own message (range cap, foreign location, bad tz) is what the user sees.
      const friendly = showApiError(e, {
        language,
        titleFallback: t('Export failed', 'Export failed. Please try again.'),
      });
      setError(friendly.message);
    } finally {
      setLoading(false);
    }
  };

  const backToHub = () => webNavigate(`/${languagePrefix}/pos-reports`);

  // Shared input style — same height + radius across pickers and select.
  const inputStyle: React.CSSProperties = {
    height: 44,
    borderRadius: 10,
    border: '1px solid #DBE4E4',
    padding: '0 12px',
    background: '#FFFFFF',
    color: '#222525',
    fontSize: 14,
    fontFamily: 'inherit',
    outline: 'none',
    width: '100%',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 12,
    color: '#4A5353',
    fontWeight: 500,
    letterSpacing: '0.02em',
  };

  return (
    <div
      className="h-full"
      style={{
        overflowY: 'auto',
        padding: isMobile ? '4px 0 80px' : '4px 0 32px',
      }}
    >
      {/* Breadcrumb */}
      <div
        className="flex items-center"
        style={{ gap: 8, marginBottom: 8, flexWrap: 'wrap' }}
      >
        <button
          type="button"
          onClick={backToHub}
          className="font-inter"
          style={{
            color: '#4A5353',
            fontSize: 13,
            background: 'transparent',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = '#0F7679')}
          onMouseLeave={(e) => (e.currentTarget.style.color = '#4A5353')}
        >
          ← {t('POS Reports', 'POS Reports')}
        </button>
        <span aria-hidden style={{ color: '#9CA3AF' }}>/</span>
        <span
          className="font-inter"
          style={{ color: '#222525', fontSize: 13, fontWeight: 500 }}
        >
          {t(title, title)}
        </span>
      </div>

      {/* Title block */}
      <p
        className="font-inter"
        style={{
          fontSize: isMobile ? '20px' : '24px',
          fontWeight: 500,
          color: '#222525',
          lineHeight: 1.2,
        }}
      >
        {t(title, title)}
      </p>
      <p
        className="font-inter"
        style={{
          marginTop: 6,
          fontSize: isMobile ? '13px' : '14px',
          color: '#4A5353',
          lineHeight: 1.5,
          maxWidth: 720,
        }}
      >
        {t(subtitle, subtitle)}
      </p>

      <div
        style={{
          marginTop: isMobile ? 20 : 28,
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 16 : 24,
          alignItems: 'flex-start',
        }}
      >
        {/* Filters card */}
        <div
          style={{
            padding: isMobile ? '16px' : '24px',
            borderRadius: 12,
            border: '1px solid #EBF0F0',
            background: '#FFFFFF',
            flex: '1 1 auto',
            minWidth: 0,
            width: '100%',
            boxShadow: '0 1px 0 rgba(15, 41, 42, 0.02)',
          }}
        >
          {/* Quick-filter chip row. Horizontally scrollable on narrow
              viewports so the strip never wraps awkwardly. */}
          <div
            role="tablist"
            aria-label={t('Quick filters', 'Quick filters')}
            style={{
              display: 'flex',
              gap: 8,
              marginBottom: 16,
              overflowX: 'auto',
              paddingBottom: 4,
              // Hide the scrollbar but keep behaviour — common on chip strips.
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            }}
          >
            <PresetChip
              active={preset === 'today'}
              onClick={() => applyPreset('today')}
              label={t('Today', 'Today')}
            />
            <PresetChip
              active={preset === 'yesterday'}
              onClick={() => applyPreset('yesterday')}
              label={t('Yesterday', 'Yesterday')}
            />
            <PresetChip
              active={preset === 'this-week'}
              onClick={() => applyPreset('this-week')}
              label={t('This week', 'This week')}
            />
            <PresetChip
              active={preset === 'last-week'}
              onClick={() => applyPreset('last-week')}
              label={t('Last week', 'Last week')}
            />
            <PresetChip
              active={preset === 'mtd'}
              onClick={() => applyPreset('mtd')}
              label={t('MTD', 'MTD')}
            />
            <PresetChip
              active={preset === 'custom'}
              onClick={() => applyPreset('custom')}
              label={t('Custom', 'Custom')}
            />
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile
                ? '1fr 1fr'
                : 'minmax(150px, 200px) minmax(150px, 200px) minmax(200px, 280px) auto',
              gap: 12,
              alignItems: 'end',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label className="font-inter" style={labelStyle}>
                {t('From', 'From')}
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="font-inter"
                style={inputStyle}
                onFocus={(e) => (e.target.style.borderColor = '#0F7679')}
                onBlur={(e) => (e.target.style.borderColor = '#DBE4E4')}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label className="font-inter" style={labelStyle}>
                {t('To', 'To')}
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="font-inter"
                style={inputStyle}
                onFocus={(e) => (e.target.style.borderColor = '#0F7679')}
                onBlur={(e) => (e.target.style.borderColor = '#DBE4E4')}
              />
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                gridColumn: isMobile ? '1 / span 2' : 'auto',
              }}
            >
              <label className="font-inter" style={labelStyle}>
                {t('Location', 'Location')}
              </label>
              <select
                value={locationId}
                onChange={(e) => setLocationId(e.target.value)}
                className="font-inter"
                style={inputStyle}
                onFocus={(e) => (e.target.style.borderColor = '#0F7679')}
                onBlur={(e) => (e.target.style.borderColor = '#DBE4E4')}
              >
                <option value="">{t('All locations', 'All locations')}</option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.locationName ?? loc.name ?? loc.id}
                  </option>
                ))}
              </select>
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                gridColumn: isMobile ? '1 / span 2' : 'auto',
              }}
            >
              <label className="font-inter" style={labelStyle}>
                {t('Format', 'Format')}
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <FormatButton
                  active={format === 'xlsx'}
                  onClick={() => setFormat('xlsx')}
                  label="XLSX"
                  full={!xlsxOnly ? false : true}
                />
                {!xlsxOnly && (
                  <FormatButton
                    active={format === 'pdf'}
                    onClick={() => setFormat('pdf')}
                    label="PDF"
                  />
                )}
              </div>
            </div>
          </div>

          {rangeError && (
            <div
              role="alert"
              className="font-inter"
              style={{
                marginTop: 16,
                fontSize: 13,
                color: '#B91C1C',
                background: '#FEF2F2',
                border: '1px solid #FECACA',
                padding: '8px 12px',
                borderRadius: 8,
              }}
            >
              {rangeError}
            </div>
          )}

          <div
            style={{
              marginTop: 20,
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: isMobile ? 'stretch' : 'center',
              gap: 12,
            }}
          >
            <button
              type="button"
              onClick={handleGenerate}
              disabled={!canSubmit}
              className="font-inter"
              style={{
                backgroundColor: !canSubmit ? '#9CA3AF' : '#0F7679',
                color: '#FFFFFF',
                fontWeight: 600,
                fontSize: 14,
                height: 44,
                padding: '0 20px',
                borderRadius: 10,
                border: 'none',
                cursor: !canSubmit ? 'not-allowed' : 'pointer',
                minWidth: isMobile ? '100%' : 160,
                transition: 'background-color 160ms ease',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
              }}
            >
              {loading && (
                <span
                  aria-hidden
                  className="report-spin"
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: '50%',
                    border: '2px solid rgba(255,255,255,0.4)',
                    borderTopColor: '#FFFFFF',
                    display: 'inline-block',
                  }}
                />
              )}
              {loading
                ? t('Generating...', 'Generating...')
                : t('Generate report', 'Generate report')}
            </button>

            {lastFilename && !loading && !error && (
              <span
                className="font-inter"
                style={{
                  color: '#0F7679',
                  fontSize: 13,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  flexWrap: 'wrap',
                  wordBreak: 'break-all',
                }}
              >
                <span aria-hidden>✓</span>
                {t('Downloaded', 'Downloaded')}: {lastFilename}
              </span>
            )}
            {error && (
              <span
                role="alert"
                className="font-inter"
                style={{
                  color: '#B91C1C',
                  fontSize: 13,
                  wordBreak: 'break-word',
                }}
              >
                {error}
              </span>
            )}
          </div>

          {truncated && !loading && !error && (
            <div
              role="status"
              className="font-inter"
              style={{
                marginTop: 12,
                fontSize: 13,
                color: '#92400E',
                background: '#FFFBEB',
                border: '1px solid #FDE68A',
                padding: '8px 12px',
                borderRadius: 8,
              }}
            >
              {t(
                'Export truncated at 50,000 lines — narrow the date range',
                'Export truncated at 50,000 lines — narrow the date range',
              )}
            </div>
          )}
        </div>

        {/* Bullets / "what's included" card */}
        {bullets && bullets.length > 0 && (
          <div
            style={{
              padding: isMobile ? '16px' : '20px',
              borderRadius: 12,
              border: '1px solid #EBF0F0',
              background: '#F8FAFA',
              width: isMobile ? '100%' : 'auto',
              minWidth: isMobile ? 0 : 260,
              maxWidth: isMobile ? '100%' : 340,
              flex: isMobile ? '0 0 auto' : '0 0 auto',
            }}
          >
            <p
              className="font-inter"
              style={{
                marginBottom: 12,
                fontSize: 12,
                fontWeight: 600,
                color: '#4A5353',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
              }}
            >
              {t('Included columns', 'Included columns')}
            </p>
            <ul style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {bullets.map((b) => (
                <li
                  key={b}
                  className="font-inter"
                  style={{
                    fontSize: 13,
                    color: '#222525',
                    paddingLeft: 14,
                    position: 'relative',
                    lineHeight: 1.5,
                  }}
                >
                  <span
                    aria-hidden
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 8,
                      width: 4,
                      height: 4,
                      borderRadius: 4,
                      background: '#0F7679',
                    }}
                  />
                  {t(b, b)}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Tiny inline keyframes — kept local so we don't touch the host
          shell's global stylesheet for a one-off spinner. */}
      <style>{`
        @keyframes report-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        .report-spin {
          animation: report-spin 800ms linear infinite;
        }
      `}</style>
    </div>
  );
};

interface PresetChipProps {
  active: boolean;
  onClick: () => void;
  label: string;
}

/**
 * Pill-style quick-filter button. Active state mirrors the Format toggle
 * (teal fill + teal border) so the filter card feels consistent.
 */
const PresetChip: React.FC<PresetChipProps> = ({ active, onClick, label }) => (
  <button
    type="button"
    role="tab"
    aria-selected={active}
    onClick={onClick}
    className="font-inter"
    style={{
      flex: '0 0 auto',
      height: 44,
      padding: '0 12px',
      borderRadius: 999,
      border: `1px solid ${active ? '#0F7679' : '#DBE4E4'}`,
      background: active ? '#E7F1F1' : '#FFFFFF',
      color: active ? '#0F7679' : '#4A5353',
      fontWeight: active ? 600 : 500,
      fontSize: 12,
      letterSpacing: '0.02em',
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      transition: 'all 120ms ease',
    }}
  >
    {label}
  </button>
);

interface FormatButtonProps {
  active: boolean;
  onClick: () => void;
  label: string;
  full?: boolean;
}

const FormatButton: React.FC<FormatButtonProps> = ({
  active,
  onClick,
  label,
  full,
}) => (
  <button
    type="button"
    onClick={onClick}
    className="font-inter"
    style={{
      flex: 1,
      height: 44,
      minWidth: full ? '100%' : 72,
      borderWidth: 1,
      borderStyle: 'solid',
      borderColor: active ? '#0F7679' : '#DBE4E4',
      borderRadius: 10,
      background: active ? '#E7F1F1' : '#FFFFFF',
      color: active ? '#0F7679' : '#222525',
      fontWeight: active ? 600 : 500,
      fontSize: 13,
      cursor: 'pointer',
      transition: 'all 120ms ease',
    }}
  >
    {label}
  </button>
);

export default ReportPageShell;
